/**
 * Today's "Notices" row — V109-1-19 R3 (Medium "Notices"). The caller's own unread in-app notices
 * (GET /api/w/:slug/notifications?unread=1 — any member, their own rows only), newest first, each
 * linking to where it is fixed (`href`, always a path inside this workspace) and marked read when
 * opened or dismissed. Nothing shows when there is nothing unread.
 * Punch list P1 (WCAG 2.4.3): after Mark read, focus moves to the next notice (else the previous);
 * after the last one or Mark all read, to the next section's heading — never to the page body.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@aigovops/design-system';
import { listNotices, markAllNoticesRead, markNoticeRead, type NoticeRow } from '../../lib/api';

export const noticesKey = (slug: string) => ['notices', slug] as const;

/** Where focus goes once the list re-renders: a notice by id, or the element after the section. */
type FocusTarget = { id: string } | { after: Element | null };

/** The heading of the element after the section (made focusable), else the main landmark. */
function afterTarget(el: Element | null): HTMLElement | null {
  const h = (el?.querySelector('h2, h3') ?? el) as HTMLElement | null;
  const t = h ?? document.querySelector<HTMLElement>('main, [role="main"]');
  if (t && !t.hasAttribute('tabindex') && !(t instanceof HTMLAnchorElement || t instanceof HTMLButtonElement)) t.setAttribute('tabindex', '-1');
  return t;
}

export function TodayNotices({ slug, announce }: { slug: string; announce?: (m: string) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: noticesKey(slug), queryFn: () => listNotices(slug, { unread: true, limit: 3 }), retry: false, staleTime: 30_000 });
  const rows = q.data?.notifications ?? [];
  const section = useRef<HTMLElement | null>(null);
  const [pending, setPending] = useState<FocusTarget | null>(null);

  useEffect(() => {
    if (!pending || q.isFetching) return;
    if ('id' in pending) {
      const li = [...(section.current?.querySelectorAll<HTMLElement>('[data-notice]') ?? [])].find((e) => e.dataset.notice === pending.id);
      const target = li?.querySelector<HTMLElement>('a, button');
      if (target) { target.focus(); setPending(null); return; }
      if (rows.length) return; // the list hasn't caught up yet
    }
    afterTarget('after' in pending ? pending.after : section.current?.nextElementSibling ?? null)?.focus();
    setPending(null);
  }, [pending, rows, q.isFetching]);

  if (!rows.length) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: noticesKey(slug) });
  /** The notice to land on after `n` goes: the next one, else the previous one, else past the section. */
  const nextFocus = (n: NoticeRow): FocusTarget => {
    const i = rows.findIndex((r) => r.notification_id === n.notification_id);
    const near = rows[i + 1] ?? rows[i - 1];
    return near ? { id: near.notification_id } : { after: section.current?.nextElementSibling ?? null };
  };
  const read = (n: NoticeRow, moveFocus: boolean) => {
    if (moveFocus) setPending(nextFocus(n));
    void markNoticeRead(slug, n.notification_id).then(refresh, () => undefined);
  };
  const more = (q.data?.unread ?? rows.length) - rows.length;
  return (
    <section ref={section} className="gp-sec" aria-labelledby="notices-h" data-part="today-notices">
      <div className="gp-sec-head">
        <h2 id="notices-h" className="gp-sec-h">Notices</h2>
        <span className="gp-sec-count">Unread <b>{q.data?.unread ?? rows.length}</b></span>
      </div>
      <ul className="gp-owed-list" aria-label="Unread notices">
        {rows.map((n) => (
          <li key={n.notification_id} className="gp-ow" data-type={n.type} data-notice={n.notification_id}>
            <span className="gp-ow-main">
              {n.href ? <Link className="gp-ow-t" to={n.href} onClick={() => read(n, false)}>{n.title}</Link> : <span className="gp-ow-t">{n.title}</span>}
              {n.body ? <span className="gp-ow-meta">{n.body}</span> : null}
            </span>
            <span className="gp-ow-act">
              <Button variant="ghost" compact onClick={() => { read(n, true); announce?.('Notice marked read.'); }}>Mark read<span className="sr-only">{`: ${n.title}`}</span></Button>
            </span>
          </li>
        ))}
      </ul>
      {more > 0 || rows.length > 1 ? (
        <Button variant="ghost" compact onClick={() => {
          setPending({ after: section.current?.nextElementSibling ?? null });
          void markAllNoticesRead(slug).then(refresh).then(() => announce?.('All notices marked read.'));
        }}>
          {more > 0 ? `Mark all ${q.data?.unread ?? rows.length} read` : 'Mark all read'}
        </Button>
      ) : null}
    </section>
  );
}
